import { X, Loader2, CalendarDays, ShieldCheck, Mic } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import ComplianceEditor from "@/components/ComplianceEditor";
import AvailabilityEditor from "@/components/AvailabilityEditor";
import TagInput from "@/components/TagInput";
import PasswordHint from "./PasswordHint";
import CertificationsEditor from "./CertificationsEditor";
import AdminVoiceFillModal from "@/components/AdminVoiceFillModal";

/**
 * Shared create + edit form for users (caregivers + admins). Stateless
 * w.r.t. the form data — the parent owns `form` and passes `setForm`.
 * Props decide which optional sections render (password, active toggle).
 */
export default function UserModal({
  title,
  form,
  setForm,
  onClose,
  onSave,
  saving,
  showPassword = false,
  showActive = false,
  isEdit = false,
}) {
  const [voiceOpen, setVoiceOpen] = useState(false);
  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4"
      onClick={onClose}
      data-testid="user-modal"
    >
      <div
        className="bg-white sm:rounded-2xl border-t sm:border border-clinical-border w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white flex items-center justify-between px-5 py-4 border-b border-clinical-border">
          <h3 className="font-heading text-lg font-medium">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="btn-ghost p-1.5"
            data-testid="user-modal-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-5 space-y-5">
          {/* Basics */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label-eyebrow mb-1.5 block">Full name</label>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="input-clinical"
                data-testid="user-name-input"
              />
            </div>
            <div>
              <label className="label-eyebrow mb-1.5 block">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="input-clinical"
                data-testid="user-email-input"
              />
            </div>
            <div>
              <label className="label-eyebrow mb-1.5 block">Role</label>
              <select
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="input-clinical"
                data-testid="user-role-select"
              >
                <option value="caregiver">Caregiver</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            {showActive && (
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm text-clinical-text cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.active !== false}
                    onChange={(e) => setForm({ ...form, active: e.target.checked })}
                    className="accent-clinical-primary"
                    data-testid="user-active-toggle"
                  />
                  Account active
                </label>
              </div>
            )}
            {showPassword && (
              <div className="sm:col-span-2">
                <label className="label-eyebrow mb-1.5 block">Temporary password</label>
                <input
                  type="text"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="e.g. Welcome123"
                  className="input-clinical font-mono"
                  data-testid="user-password-input"
                />
                <PasswordHint value={form.password || ""} />
                <p className="mt-1 text-xs text-clinical-subtext">
                  Share privately. User can change it later via the reset-password flow.
                </p>
              </div>
            )}
          </div>

          {/* Contact */}
          <div className="border-t border-clinical-border pt-4">
            <div className="label-eyebrow mb-2">Contact</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-clinical-subtext block mb-1">Phone</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="input-clinical"
                  data-testid="user-phone-input"
                />
              </div>
              <div>
                <label className="text-xs text-clinical-subtext block mb-1">Address</label>
                <input
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  className="input-clinical"
                  data-testid="user-address-input"
                />
              </div>
              <div>
                <label className="text-xs text-clinical-subtext block mb-1">Date of birth</label>
                <input
                  type="date"
                  value={form.dob}
                  onChange={(e) => setForm({ ...form, dob: e.target.value })}
                  className="input-clinical"
                  data-testid="user-dob-input"
                />
              </div>
            </div>
          </div>

          {/* Employment */}
          <div className="border-t border-clinical-border pt-4">
            <div className="label-eyebrow mb-2">Employment</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-clinical-subtext block mb-1">Hire date</label>
                <input
                  type="date"
                  value={form.hire_date}
                  onChange={(e) => setForm({ ...form, hire_date: e.target.value })}
                  className="input-clinical"
                  data-testid="user-hire-date-input"
                />
              </div>
              <div>
                <label className="text-xs text-clinical-subtext block mb-1">Hourly rate (USD)</label>
                <input
                  type="number"
                  step="0.01"
                  value={form.hourly_rate}
                  onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })}
                  placeholder="e.g. 28.50"
                  className="input-clinical"
                  data-testid="user-hourly-rate-input"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs text-clinical-subtext block mb-1">Worker ID (for EVV)</label>
                <input
                  value={form.worker_id || ""}
                  onChange={(e) => setForm({ ...form, worker_id: e.target.value })}
                  placeholder="NPI or state-issued caregiver ID"
                  className="input-clinical"
                  data-testid="user-worker-id-input"
                />
                <p className="text-[11px] text-clinical-subtext mt-1">
                  Required for Sandata / HHAeXchange visit submission.
                </p>
              </div>
            </div>
          </div>

          {/* Certifications */}
          <div className="border-t border-clinical-border pt-4">
            <div className="label-eyebrow mb-2">Certifications & licenses</div>
            <CertificationsEditor
              items={form.certifications}
              onChange={(arr) => setForm({ ...form, certifications: arr })}
              testidPrefix={isEdit ? "edit-cert" : "new-cert"}
            />
          </div>

          {/* Compliance tracking */}
          <div className="border-t border-clinical-border pt-4">
            <div className="label-eyebrow mb-2 flex items-center gap-1.5">
              <ShieldCheck className="h-3 w-3" /> Compliance & verification
            </div>
            <p className="text-xs text-clinical-subtext mb-2">
              Track background checks, CPR, TB test, licenses, insurance, etc. Verified items
              power the family-trust badges.
            </p>
            <ComplianceEditor
              items={form.compliance_items}
              onChange={(arr) => setForm({ ...form, compliance_items: arr })}
              testidPrefix={isEdit ? "edit-compliance" : "new-compliance"}
            />
          </div>

          {/* Availability — caregivers only */}
          {form.role === "caregiver" && (
            <div className="border-t border-clinical-border pt-4">
              <div className="label-eyebrow mb-2 flex items-center gap-1.5">
                <CalendarDays className="h-3 w-3" /> Availability
              </div>
              <p className="text-xs text-clinical-subtext mb-2">
                Set the caregiver's working hours and time-off. The scheduler uses this to flag
                conflicts when assigning visits.
              </p>
              <AvailabilityEditor
                value={form.availability}
                onChange={(av) => setForm({ ...form, availability: av })}
                testidPrefix={isEdit ? "edit-availability" : "new-availability"}
              />
            </div>
          )}

          {/* Emergency contact */}
          <div className="border-t border-clinical-border pt-4">
            <div className="label-eyebrow mb-2">Emergency contact</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input
                value={form.emergency_contact_name}
                onChange={(e) => setForm({ ...form, emergency_contact_name: e.target.value })}
                placeholder="Name"
                className="input-clinical"
                data-testid="user-emc-name-input"
              />
              <input
                type="tel"
                value={form.emergency_contact_phone}
                onChange={(e) => setForm({ ...form, emergency_contact_phone: e.target.value })}
                placeholder="Phone"
                className="input-clinical"
                data-testid="user-emc-phone-input"
              />
            </div>
          </div>

          {/* Skills & languages — used by the client/caregiver match scoring */}
          <div className="border-t border-clinical-border pt-4 space-y-3">
            <div>
              <label className="label-eyebrow mb-1.5 block">Skills</label>
              <TagInput
                value={form.skills || []}
                onChange={(v) => setForm({ ...form, skills: v })}
                suggestions={["dementia-care", "hoyer-lift", "wound-care", "cpr", "first-aid", "g-tube", "hospice", "alzheimers"]}
                placeholder="e.g. dementia-care, hoyer-lift, cpr — Enter to add"
                testid="user-skills-input"
              />
              <p className="text-[11px] text-clinical-subtext mt-1">
                Used to match this caregiver to clients with specific care needs.
              </p>
            </div>
            <div>
              <label className="label-eyebrow mb-1.5 block">Languages spoken</label>
              <TagInput
                value={form.languages || []}
                onChange={(v) => setForm({ ...form, languages: v })}
                suggestions={["en", "es", "fr", "ar", "pt", "tl", "vi", "zh", "hi"]}
                placeholder="en, es, fr…"
                testid="user-languages-input"
              />
            </div>
          </div>

          {/* Admin notes */}
          <div className="border-t border-clinical-border pt-4">
            <div className="flex items-center justify-between mb-1.5">
              <label className="label-eyebrow">Admin notes</label>
              <button
                type="button"
                onClick={() => setVoiceOpen(true)}
                className="inline-flex items-center gap-1 rounded-lg bg-purple-100 px-2 py-1 text-[11px] font-semibold text-purple-700 hover:bg-purple-200"
                data-testid="user-notes-voice-btn"
              >
                <Mic size={11} /> Voice note
              </button>
            </div>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2}
              placeholder="Internal notes — not visible to caregiver."
              className="input-clinical resize-y"
              data-testid="user-notes-input"
            />
            <AdminVoiceFillModal
              open={voiceOpen}
              onClose={() => setVoiceOpen(false)}
              endpoint="/ai/voice-fill/caregiver-note"
              title="Voice caregiver note"
              hint='e.g. "Sarah was 10 minutes late twice this month but otherwise excellent — kudos for the dementia training initiative."'
              examples={[
                "Sarah was 10 minutes late twice this month but otherwise excellent — kudos for the dementia training.",
                "Concern: Maria has missed two shifts without calling. Need a 1 on 1 this week.",
                "Promote James to senior caregiver — Margaret's family loves him.",
              ]}
              limitMs={90 * 1000}
              filenamePrefix="cgnote"
              testIdPrefix="cgnote-voice"
              renderPreview={(r) => (
                <div className="space-y-2 text-sm">
                  {r.note && (
                    <div>
                      <div className="label-eyebrow">Structured note</div>
                      <p className="text-clinical-text">{r.note}</p>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2 text-xs">
                    {r.sentiment && (
                      <span className={`pill ${
                        r.sentiment === "positive" ? "bg-green-100 text-green-800" :
                        r.sentiment === "critical" ? "bg-red-100 text-red-800" :
                        r.sentiment === "concern" ? "bg-amber-100 text-amber-900" :
                        "bg-clinical-bg text-clinical-subtext"
                      }`}>{r.sentiment}</span>
                    )}
                    {(r.tags || []).map((t) => (
                      <span key={t} className="pill bg-clinical-bg text-clinical-subtext">{t}</span>
                    ))}
                    {r.followup_required && (
                      <span className="pill bg-amber-100 text-amber-900">follow-up required</span>
                    )}
                  </div>
                </div>
              )}
              onConfirm={(r) => {
                const stamp = new Date().toISOString().slice(0, 10);
                const block = [
                  `[${stamp}] ${r.note || r.transcript}`,
                  r.sentiment ? `Sentiment: ${r.sentiment}` : "",
                  (r.tags || []).length ? `Tags: ${(r.tags || []).join(", ")}` : "",
                  r.followup_required ? "⚠ Follow-up required" : "",
                ].filter(Boolean).join(" · ");
                setForm({
                  ...form,
                  notes: form.notes ? `${form.notes}\n\n${block}` : block,
                });
                toast.success("Voice note added");
              }}
              confirmLabel="Append to notes"
            />
          </div>
        </div>

        <div className="sticky bottom-0 bg-white flex items-center justify-end gap-2 px-5 py-4 border-t border-clinical-border">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="btn-secondary"
            data-testid="user-modal-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            className="btn-primary"
            data-testid="user-modal-save"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : isEdit ? (
              "Save changes"
            ) : (
              "Create user"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
